import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ButlerConfiguration } from '../../models/ButlerConfiguration';
import { ActivatedRoute, NavigationEnd, Router, RouterState } from '@angular/router';
import { ApiService } from '../../services/api.service';
import { StepperService } from '../../services/stepper.service';
import { Subscription } from 'rxjs';
import { ThemeService } from '../../services/theme.service';
import { Theme } from '../../models/Theme';
import { SelectCountryService } from '../../services/select-country.service';
import { LegalService } from '../../services/legal.service';
import { ToastService } from '../../services/toast.service';
import { Title } from '@angular/platform-browser';
import { CookieConsentService } from '../../services/cookie-consent.service';
import { ConfigurationStepType } from 'src/app/models/ConfigurationStep';

declare let gtag: Function;

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy {
  private stepperSubscription!: Subscription;
  private configurationSubscription!: Subscription;
  private darkModeSubscription!: Subscription;
  private selectCountryOpenSubscription!: Subscription;
  private cookieToastSubscription!: Subscription;
  private bookingDetailsSubscription!: Subscription;
  private viewLegalOpenSubscription!: Subscription;

  public configurationStepType: ConfigurationStepType = ConfigurationStepType.NO_CONFIRMATION;
  public configuration: ButlerConfiguration | null = null;
  public index = -1;
  public currentStep = this.getStepName(this.index);
  public screenWidth: number = 0;
  public isSmallScreen = true;
  public screenHeight: number = 0;
  public darkMode: boolean | undefined;
  public selectCountryOpen = false;
  public bookingDetails: any = {};
  public toastVisible: boolean = true;
  public viewLegalOpen: boolean = false;


  constructor(private activatedRoute: ActivatedRoute,
              private stepperService: StepperService,
              private themeService: ThemeService,
              private apiService: ApiService,
              private selectCountryService: SelectCountryService,
              private router: Router,
              private titleService: Title,
              private cookieConsentService: CookieConsentService,
              private legalService: LegalService,
              private toastService: ToastService) {

  }

  ngOnInit(): void {
    this.setWidth();
    this.setStepperSubscription();
    this.setConfigurationSubscription();
    this.setCountrySubscription();
    this.setThemeSubscription();
    this.handleTripRetry();
    this.setToastSubscription();
    this.setLegalSubscriptions();
  }

  ngOnDestroy(): void {
    this.stepperService.reset();
    this.stepperSubscription.unsubscribe();
    this.configurationSubscription.unsubscribe();
    this.darkModeSubscription.unsubscribe();
    this.selectCountryOpenSubscription.unsubscribe();
    this.cookieToastSubscription.unsubscribe();
    this.viewLegalOpenSubscription.unsubscribe();
    this.handleRouteEvents();
  }

  private setThemeSubscription(): void {
    this.darkModeSubscription = this.themeService.isDarkTheme$.subscribe(darkMode => this.darkMode = darkMode);
  }

  private setToastSubscription(): void {
    this.cookieToastSubscription = this.toastService.cookieToastVisible$.subscribe(visible => this.toastVisible = visible);
  }

  private setCountrySubscription(): void {
    this.selectCountryOpenSubscription = this.selectCountryService.selectCountryOpen$
      .subscribe(open => this.selectCountryOpen = open);
  }

  private setLegalSubscriptions(): void {
    this.viewLegalOpenSubscription = this.legalService.viewLegalOpen$.subscribe(open => this.viewLegalOpen = open);
  }

  private setConfigurationSubscription(): void {
    this.configurationSubscription = this.apiService.butlerConfiguration$.subscribe(configuration => {
      if (configuration) {
        this.configuration = configuration;
        this.configurationStepType = configuration.configurationSteps[configuration.configurationSteps.length - 1].type;
        this.themeService.updateTheme(configuration.themeDetails);
        this.stepperService.setAmountOfSteps(configuration.configurationSteps.length);
        this.themeService.updateColorMode(configuration.themeDetails.theme === Theme.DARK.toString());
      }
    });

    this.bookingDetailsSubscription = this.stepperService.bookingDetails$.subscribe(bookingDetails => {
      this.bookingDetails = bookingDetails;
    });
  }

  private setStepperSubscription(): void {
    this.stepperSubscription = this.stepperService.index$.subscribe(index => {
      this.index = index;
      this.currentStep = this.getStepName(this.index);
    });
  }

  private handleTripRetry() {
    const retryTrip = this.activatedRoute.snapshot.queryParamMap.get('retry');
    if (retryTrip === 'true' && localStorage.getItem('bookingDetails')) {
      let bookingDetails = JSON.parse(localStorage.getItem('bookingDetails')!);
      if (bookingDetails.expirationDate > new Date().getTime()) {
        this.stepperService.updateBookingDetails(bookingDetails);
        this.stepperService.jumpToBookingOverview();
      }
    }
  }

  public next = (): void => {
    this.stepperService.next();
  };

  public previous = (): void => {
    this.stepperService.previous();
  };

  getStepName(index: number): string | null {
    if (this.configuration && index > -1) {
      return this.configuration.configurationSteps[index].type.toString();
    } else {
      return null;
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.setWidth();
  }

  private setWidth(): void {
    this.screenWidth = window.innerWidth;
    this.isSmallScreen = window.innerWidth < 600;
    this.screenHeight = window.innerHeight;
  }

  parseInt(screenHeight: string): number {
    return parseInt(screenHeight);
  }

  handleRouteEvents(): void {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        const title = this.getTitle(this.router.routerState, this.router.routerState.root).join('-');
        this.titleService.setTitle(title);
        if (this.cookieConsentService.getTrackingConsent()) {
          gtag('event', 'page_view', {
            page_title: title,
            page_path: event.urlAfterRedirects,
            page_location: window.location.href
          });
        }
      }
    });
  }

  isMobile() {
    let check = false;
    (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor);
    return check;
  };

  getTitle(state: RouterState, parent: ActivatedRoute): string[] {
    const data = [];
    if (parent && parent.snapshot.data && parent.snapshot.data['title']) {
      data.push(parent.snapshot.data['title']);
    }
    if (state && parent && parent.firstChild) {
      data.push(...this.getTitle(state, parent.firstChild));
    }
    return data;
  }
}
